// Lightweight analytics helper. Fires anonymous, salted events to the backend.
// Throttles duplicate page views (handled server-side too).
//
// Usage:
//   import { trackPageview, trackEvent } from "@/lib/analytics";
//   trackPageview(location.pathname);
//   trackEvent("download_click");
import { api } from "@/lib/api";
import { hasAnalyticsConsent } from "@/lib/consent";

const SESSION_KEY = "prs_session_id";
const LAST_PV_KEY = "prs_last_pv";

function genSessionId() {
    try {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    } catch {
        /* fallthrough */
    }
    return "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getSessionId() {
    try {
        let id = localStorage.getItem(SESSION_KEY);
        if (!id) {
            id = genSessionId();
            localStorage.setItem(SESSION_KEY, id);
        }
        return id;
    } catch {
        return "anon";
    }
}

function isAdminPath(path = "") {
    return path.startsWith("/admin");
}

async function postEvent(payload) {
    try {
        await api.post("/analytics/event", payload, {
            // Don't let analytics failures bubble up
            timeout: 4000,
        });
    } catch {
        /* swallow */
    }
}

export async function trackPageview(path) {
    if (typeof window === "undefined") return;
    // GDPR: skip analytics entirely if the user hasn't opted in.
    if (!hasAnalyticsConsent()) return;
    const p = path || window.location.pathname || "/";
    if (isAdminPath(p)) return; // don't count admin browsing

    // Local 25s de-dup (server also de-dupes)
    try {
        const last = JSON.parse(sessionStorage.getItem(LAST_PV_KEY) || "null");
        const now = Date.now();
        if (last && last.path === p && now - last.t < 25_000) return;
        sessionStorage.setItem(LAST_PV_KEY, JSON.stringify({ path: p, t: now }));
    } catch {
        /* ignore storage errors */
    }

    await postEvent({
        type: "pageview",
        path: p,
        referrer: document.referrer || "",
        session_id: getSessionId(),
    });
}

export async function trackEvent(type, extra = {}) {
    if (typeof window === "undefined") return;
    if (!hasAnalyticsConsent()) return;
    if (isAdminPath(window.location.pathname || "")) return;
    await postEvent({
        type,
        path: window.location.pathname || "/",
        session_id: getSessionId(),
        label: extra.label || "",
    });
}
